import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemIcon,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { useUI } from "../contexts/UIContext";
import { FilterDisplaySettings } from "../utils/filterSettings";
import { filterSettings as defaultFilterSettings } from "../utils/filterSettings";

interface FilterSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FilterRow extends FilterDisplaySettings {
  id: string;
  key: string;
}

let filterRowIdCounter = 0;
function makeId() {
  return `filterrow_${filterRowIdCounter++}`;
}
function objectToArray(
  obj: Record<string, FilterDisplaySettings>
): FilterRow[] {
  return Object.entries(obj).map(([key, value]) => ({
    id: makeId(),
    key,
    ...value,
  }));
}
function arrayToObject(
  arr: FilterRow[]
): Record<string, FilterDisplaySettings> {
  const out: Record<string, FilterDisplaySettings> = {};
  arr.forEach(({ id, key, ...rest }) => {
    if (key.trim()) out[key] = rest;
  });
  return out;
}

const FilterRowEditor = React.memo(function FilterRowEditor({
  id,
  initialColor,
  keyValue,
  name,
  onKeyChange,
  onNameChange,
  onColorCommit,
  onDelete,
  onRequestDelete,
  isDuplicate,
}: {
  id: string;
  initialColor: string;
  keyValue: string;
  name?: string;
  onKeyChange: (id: string, newKey: string) => void;
  onNameChange: (id: string, name: string) => void;
  onColorCommit: (id: string, color: string) => void;
  onDelete: (id: string) => void;
  onRequestDelete: (id: string) => void;
  isDuplicate: boolean;
}) {
  // Uncontrolled color input: use ref
  const colorInputRef = React.useRef<HTMLInputElement>(null);
  const iconRef = React.useRef<SVGSVGElement>(null);

  // On input, update icon color directly via DOM
  const handleInput = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const color = e.currentTarget.value;
      if (iconRef.current) {
        iconRef.current.style.color = color;
      }
    },
    []
  );

  // On blur, commit color to parent
  const handleBlur = React.useCallback(() => {
    const color = colorInputRef.current?.value || initialColor;
    onColorCommit(id, color);
    // No need to update icon, parent will re-render with new color
  }, [id, onColorCommit, initialColor]);

  React.useEffect(() => {
    // Set icon color on mount and when initialColor changes
    if (iconRef.current) {
      iconRef.current.style.color = initialColor;
    }
  }, [initialColor]);

  console.log("FilterRowEditor render", id, initialColor);
  return (
    <ListItem key={id} sx={{ alignItems: "flex-start" }}>
      <ListItemIcon>
        <ColorLensIcon ref={iconRef} sx={{ fontSize: 32 }} />
      </ListItemIcon>
      <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
        <TextField
          label="Key"
          value={keyValue}
          onChange={(e) => onKeyChange(id, e.target.value)}
          size="small"
          sx={{ width: 120, mr: 1, alignSelf: "flex-start" }}
          error={isDuplicate || !keyValue.trim()}
          helperText={
            isDuplicate
              ? "Duplicate key"
              : !keyValue.trim()
              ? "Required"
              : "\u00A0"
          }
          FormHelperTextProps={{ sx: { minHeight: 20, m: 0, p: 0 } }}
        />
        <TextField
          label="Name"
          value={name || ""}
          onChange={(e) => onNameChange(id, e.target.value)}
          size="small"
          sx={{ width: 120, mr: 1, alignSelf: "flex-start" }}
          helperText={"\u00A0"}
          FormHelperTextProps={{
            sx: { minHeight: 20, m: 0, p: 0, color: "transparent" },
          }}
        />
        <TextField
          label="Color"
          type="color"
          inputRef={colorInputRef}
          defaultValue={initialColor}
          onInput={handleInput}
          onBlur={handleBlur}
          size="small"
          sx={{ width: 60, mr: 1, p: 0, minWidth: 0, alignSelf: "flex-start" }}
          InputLabelProps={{ shrink: true }}
          helperText={"\u00A0"}
          FormHelperTextProps={{
            sx: { minHeight: 20, m: 0, p: 0, color: "transparent" },
          }}
        />
        <ListItemSecondaryAction
          sx={{
            transform: "translateY(-80%) translateX(-350%)",
          }}
        >
          <IconButton edge="end" onClick={() => onRequestDelete(id)}>
            <DeleteIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </Box>
    </ListItem>
  );
});

const FilterSettingsDialog: React.FC<FilterSettingsDialogProps> = ({
  open,
  onClose,
}) => {
  const { filterSettings, filterSettingsLoading, setFilterSettings } = useUI();

  // Initialize all hooks first
  const [rows, setRows] = useState(() => {
    filterRowIdCounter = 0;
    const arr = objectToArray(filterSettings || defaultFilterSettings);
    console.log("Dialog mounted, initial rows:", arr);
    return arr;
  });

  // Update rows when filterSettings changes
  React.useEffect(() => {
    if (filterSettings) {
      filterRowIdCounter = 0;
      const arr = objectToArray(filterSettings);
      console.log("Updating rows from new filterSettings:", arr);
      setRows(arr);
    }
  }, [filterSettings]);

  const DEFAULT_COLOR = "#8884d8";

  const [newKey, setNewKey] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [newName, setNewName] = useState("");
  const [addRowTouched, setAddRowTouched] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // For Add Filter row, use the same logic as FilterRowEditor
  const colorInputRef = React.useRef<HTMLInputElement>(null);
  const iconRef = React.useRef<SVGSVGElement>(null);
  const initialColor = newColor;

  // All callbacks and effects must be defined before any conditional returns
  const handleInput = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const color = e.currentTarget.value;
      if (iconRef.current) {
        iconRef.current.style.color = color;
      }
    },
    []
  );

  const handleBlur = React.useCallback(() => {
    const color = colorInputRef.current?.value || initialColor;
    setNewColor(color);
  }, [initialColor]);

  const handleColorCommit = React.useCallback((id: string, color: string) => {
    setRows((rows) => rows.map((r) => (r.id === id ? { ...r, color } : r)));
    console.log("Color committed to parent (rows updated)", id, color);
  }, []);

  React.useEffect(() => {
    if (iconRef.current) {
      iconRef.current.style.color = initialColor;
    }
  }, [initialColor]);

  // Don't render anything if dialog is closed
  if (!open) return null;

  // Show loading state while fetching filter settings
  if (filterSettingsLoading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: "#181818",
            color: "#fff",
            borderRadius: 3,
            boxShadow: 12,
          },
        }}
      >
        <DialogContent sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const handleKeyChange = (id: string, newKeyVal: string) => {
    setRows((rows) =>
      rows.map((r) => (r.id === id ? { ...r, key: newKeyVal } : r))
    );
    console.log("Key changed", id, newKeyVal);
  };
  const handleNameChange = (id: string, name: string) => {
    setRows((rows) => rows.map((r) => (r.id === id ? { ...r, name } : r)));
    console.log("Name changed", id, name);
  };
  const handleDelete = (id: string) => {
    setRows((rows) => rows.filter((r) => r.id !== id));
    console.log("Deleted", id);
  };
  const handleAdd = () => {
    if (!newKey.trim() || rows.some((row) => row.key === newKey)) return;
    const newRow = {
      id: makeId(),
      key: newKey,
      color: newColor,
      name: newName,
    };
    setRows((rows) => [...rows, newRow]);
    setNewKey("");
    setNewColor("#8884d8");
    setNewName("");
    console.log("Added", newRow);
  };
  const handleSave = async () => {
    try {
      await setFilterSettings(arrayToObject(rows));
      console.log("Saved", rows);
      onClose();
    } catch (error) {
      // Keep dialog open if save failed
      console.error("Failed to save filter settings:", error);
    }
  };
  console.log("Dialog render", rows);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#181818",
          color: "#fff",
          borderRadius: 3,
          boxShadow: 12,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 28,
          bgcolor: "#181818",
        }}
      >
        Edit Filter Settings
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "#181818", color: "#fff" }}>
        <List>
          {rows.map((row) => {
            const isDuplicate =
              rows.filter((r) => r.key === row.key).length > 1;
            return (
              <FilterRowEditor
                key={row.id}
                id={row.id}
                initialColor={row.color}
                keyValue={row.key}
                name={row.name}
                onKeyChange={handleKeyChange}
                onNameChange={handleNameChange}
                onColorCommit={handleColorCommit}
                onDelete={handleDelete}
                onRequestDelete={setPendingDeleteId}
                isDuplicate={isDuplicate}
              />
            );
          })}
        </List>
        <Divider sx={{ mb: 1, bgcolor: "#333" }} />
        <ListItem alignItems="center" sx={{ bgcolor: "#181818" }}>
          <ListItemIcon>
            <ColorLensIcon
              ref={iconRef}
              sx={{ fontSize: 32, mb: 2.6, color: newColor }}
            />
          </ListItemIcon>
          <Box
            sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                mr: 1,
                width: 120,
              }}
            >
              <TextField
                label="Key"
                value={newKey}
                onChange={(e) => {
                  setNewKey(e.target.value);
                  if (!addRowTouched) setAddRowTouched(true);
                }}
                onFocus={() => {
                  if (!addRowTouched) setAddRowTouched(true);
                }}
                size="small"
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    bgcolor: "#222",
                    color: "#fff",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#bbb",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#444",
                  },
                }}
                error={
                  addRowTouched &&
                  (rows.some((row) => row.key === newKey) || !newKey.trim())
                }
                helperText={
                  addRowTouched
                    ? rows.some((row) => row.key === newKey)
                      ? "Duplicate key"
                      : !newKey.trim()
                      ? "Required"
                      : "\u00A0"
                    : "\u00A0"
                }
                InputLabelProps={{ sx: { color: "#bbb" } }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                mr: 1,
                width: 120,
              }}
            >
              <TextField
                label="Name"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (!addRowTouched) setAddRowTouched(true);
                }}
                onFocus={() => {
                  if (!addRowTouched) setAddRowTouched(true);
                }}
                size="small"
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    bgcolor: "#222",
                    color: "#fff",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#bbb",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#444",
                  },
                }}
                InputLabelProps={{ sx: { color: "#bbb" } }}
              />
              <Typography
                variant="caption"
                sx={{
                  minHeight: 20,
                  mt: 0.5,
                  pl: "2px",
                  fontSize: "0.75rem",
                  color: "transparent",
                }}
              >
                {"\u00A0"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                mr: 1,
                width: 60,
              }}
            >
              <TextField
                label="Color"
                type="color"
                inputRef={colorInputRef}
                defaultValue={newColor}
                onInput={handleInput}
                onBlur={handleBlur}
                onFocus={() => {
                  if (!addRowTouched) setAddRowTouched(true);
                }}
                size="small"
                sx={{
                  width: "100%",
                  p: 0,
                  minWidth: 0,
                  "& .MuiInputBase-root": {
                    bgcolor: "#222",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#444",
                  },
                }}
                InputLabelProps={{ shrink: true, sx: { color: "#bbb" } }}
              />
              <Typography
                variant="caption"
                sx={{
                  minHeight: 20,
                  mt: 0.5,
                  pl: "2px",
                  fontSize: "0.75rem",
                  color: "transparent",
                }}
              >
                {"\u00A0"}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={
                !newKey.trim() || rows.some((row) => row.key === newKey)
              }
              sx={{
                ml: 1,
                height: 40,
                width: 150,
                flexShrink: 0,
                bgcolor: "#ff6d2d",
                color: "#fff",
                fontWeight: 700,
                "&:hover": { bgcolor: "#ff944d" },
                boxShadow: "none",
              }}
            >
              Add Filter
            </Button>
          </Box>
        </ListItem>
        <Typography
          variant="caption"
          color="#bbb"
          sx={{ mt: 2, display: "block" }}
        >
          Changes are local until you click Save. Removing a filter here only
          affects the display mapping, not your data.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ bgcolor: "#181818", borderTop: "1px solid #222" }}>
        <Button onClick={onClose} sx={{ color: "#ff6d2d", fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: "#ff6d2d",
            color: "#fff",
            fontWeight: 700,
            "&:hover": { bgcolor: "#ff944d" },
            boxShadow: "none",
          }}
        >
          Save
        </Button>
      </DialogActions>
      {/* Delete Filter Confirm Dialog */}
      <Dialog
        open={!!pendingDeleteId}
        onClose={() => setPendingDeleteId(null)}
        PaperProps={{
          sx: {
            backgroundColor: "#181818",
            color: "#fff",
            borderRadius: 3,
            boxShadow: 12,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", fontWeight: 700 }}>
          Delete Filter
        </DialogTitle>
        <DialogContent sx={{ color: "#fff" }}>
          Are you sure you want to delete this filter?
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#181818", borderTop: "1px solid #222" }}>
          <Button
            onClick={() => setPendingDeleteId(null)}
            sx={{ color: "#ff6d2d", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pendingDeleteId) handleDelete(pendingDeleteId);
              setPendingDeleteId(null);
            }}
            color="error"
            variant="contained"
            sx={{
              bgcolor: "#ff6d2d",
              color: "#fff",
              fontWeight: 700,
              "&:hover": { bgcolor: "#ff944d" },
              boxShadow: "none",
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default FilterSettingsDialog;
